/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListOktaServerSuggestionsRequest,
  ListOktaServerSuggestionsSecurity,
} from "../models/operations/listoktaserversuggestions.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildOktaServerSuggestionsQuery,
  OktaServerSuggestionsQueryData,
  prefetchOktaServerSuggestions,
  queryKeyOktaServerSuggestions,
} from "./oktaServerSuggestions.core.js";
export {
  buildOktaServerSuggestionsQuery,
  type OktaServerSuggestionsQueryData,
  prefetchOktaServerSuggestions,
  queryKeyOktaServerSuggestions,
};

export type OktaServerSuggestionsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * list oktaServerSuggestions
 *
 * @remarks
 * List the catalog servers suggested from the Okta applications snapshot. Requires org:admin, the okta-connections rollout and a verified identity provider connection.
 */
export function useOktaServerSuggestions(
  request?: ListOktaServerSuggestionsRequest | undefined,
  security?: ListOktaServerSuggestionsSecurity | undefined,
  options?: QueryHookOptions<
    OktaServerSuggestionsQueryData,
    OktaServerSuggestionsQueryError
  >,
): UseQueryResult<
  OktaServerSuggestionsQueryData,
  OktaServerSuggestionsQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildOktaServerSuggestionsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * list oktaServerSuggestions
 *
 * @remarks
 * List the catalog servers suggested from the Okta applications snapshot. Requires org:admin, the okta-connections rollout and a verified identity provider connection.
 */
export function useOktaServerSuggestionsSuspense(
  request?: ListOktaServerSuggestionsRequest | undefined,
  security?: ListOktaServerSuggestionsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    OktaServerSuggestionsQueryData,
    OktaServerSuggestionsQueryError
  >,
): UseSuspenseQueryResult<
  OktaServerSuggestionsQueryData,
  OktaServerSuggestionsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildOktaServerSuggestionsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setOktaServerSuggestionsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      includeAll?: boolean | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: OktaServerSuggestionsQueryData,
): OktaServerSuggestionsQueryData | undefined {
  const key = queryKeyOktaServerSuggestions(...queryKeyBase);

  return client.setQueryData<OktaServerSuggestionsQueryData>(key, data);
}

export function invalidateOktaServerSuggestions(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      includeAll?: boolean | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "oktaServerSuggestions",
      "list",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllOktaServerSuggestions(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "oktaServerSuggestions", "list"],
  });
}
